import { useCallback, useEffect } from "react";
import {
  useExState,
  allUnitFilters,
  allTraitFilters,
  allItemFilters,
  allAugmentFilters,
} from "./ExState";
import type { Filter } from "./ExState";
import { Select, MenuItem, CircularProgress } from "@mui/material";
import * as Sd from "common/src/tft-static/Sd";
import { use as useSet } from "common/src/data/TftSet.bs";
import { allPatches } from "common/src/Config";
import { render as renderPatch } from "common/src/Patch.bs";
import { common as t } from "common/src/bindings/Translation2";

import { useRouter } from "next/router";
import ExFilterSelect from "./ExFilterSelect";
import ExDataTable from "./ExDataTable";
import { use as useAfm } from "../app-core/Afm.bs";
import { PatreonSvg } from "../patreon/Patreon";
import Link from "next/link";

const toUrlName = (id: string): string => {
  return id.split("_")[1].toLowerCase();
};

const encodeFilters = (filters: Array<Filter>, rankGroup: number): string => {
  let filtersQuery = "";
  filters.forEach((f, idx) => {
    if (filtersQuery === "") {
      filtersQuery += "?f" + (idx + 1) + "=";
    } else {
      filtersQuery += "&f" + (idx + 1) + "=";
    }
    switch (f.typ) {
      case "u":
        filtersQuery += `u_${toUrlName(f.id)}_${f.starLevel}`;
        break;
      case "t":
        filtersQuery += `t_${toUrlName(f.id)}_${f.rank}`;
        break;
      case "i":
        filtersQuery += `i_${f.id}_${toUrlName(f.unitId[0])}_${f.unitId[1]}`;
        break;
      case "a":
        filtersQuery += `a_${f.id}_${f.pick}`;
        break;
    }
  });
  if (filtersQuery != "") {
    filtersQuery += "&rg=" + rankGroup;
  }

  return filtersQuery;
};

const decodeFilters = (query: string, set: number): [Array<Filter>, number] => {
  const filters: Array<Filter> = [];
  let rankGroup = 1;
  const allUnits = Sd.allUnits(set);
  const allTraits = Sd.allTraits(set);

  for (let q of query.split("&")) {
    const [k, v] = q.split("=");
    if (["f1", "f2", "f3"].includes(k)) {
      const parts = v.split("_");
      switch (parts[0]) {
        case "u":
          const unitId = allUnits.find((uid) => toUrlName(uid) === parts[1])!;
          filters.push({
            ...allUnitFilters().find((f) => f.id === unitId)!,
            starLevel: Number(parts[2]),
          });
          break;
        case "t":
          const traitId = allTraits.find((tid) => toUrlName(tid) === parts[1])!;
          filters.push(
            allTraitFilters().find(
              (f) => f.id === traitId && f.rank === Number(parts[2]),
            )!,
          );
          break;
        case "i":
          const uid = allUnits.find((uid) => toUrlName(uid) === parts[2])!;
          filters.push({
            ...allItemFilters().find((f) => f.id === parts[1])!,
            unitId: [uid, Number(parts[3])],
          });
          break;
        case "a":
          filters.push({
            ...allAugmentFilters().find((f) => f.id === parts[1])!,
            pick: Number(parts[2]),
          });
          break;
      }
    } else if (k === "rg") {
      rankGroup = Number(v);
    }
  }

  return [filters, rankGroup];
};

export default function ExPage() {
  const afm = useAfm();
  const set = useSet();
  const router = useRouter();
  const [state, dispatch, fullData, getMatches] = useExState();

  useEffect(() => {
    const query = router.asPath.split("?")[1];
    if (query) {
      const [filters, rankGroup] = decodeFilters(
        router.asPath.split("?")[1],
        set,
      );
      // TODO FIXME
      // dispatch(["set_init_state", [filters, rankGroup]]);
    }
  }, [router.asPath]);

  const onExpand = useCallback(
    (filter: Filter) => dispatch(["add_filter", filter]),
    [dispatch],
  );

  useEffect(() => {
    const filtersQuery = encodeFilters(state.filters, state.rankGroup);
    router.push(`/explorer${filtersQuery}`, undefined, { shallow: true });
  }, [state.filters, state.rankGroup]);

  return (
    <div className="flex flex-col items-center flex-shrink-1">
      <Link href={afm ? "/explorer-advanced" : "/patreon"}>
        <div className="flex items-start gap-4 bg-bg rounded p-4 mt-8 mb-4 border-primary-10a border-2 max-w-[650px] leading-snug">
          {afm ? (
            <div>
              Thanks for your support on Patreon.{" "}
              <span className="text-primary-11">Click here</span> to switch to
              the advanced explorer.
            </div>
          ) : (
            <>
              <PatreonSvg active={false} size={40} />
              <div>
                Advanced explorer with unlimited filters and additional options
                is available to patrons.{" "}
                <span className="text-primary-11">Click here</span> to learn
                more about becoming a patron.
              </div>
            </>
          )}
        </div>
      </Link>
      <div>
        <div className="p-4 mt-4 bg-bg flex items-center justify-between">
          <h2 className="text-montserrat text-2xl">{t("Stats Explorer")}</h2>
          <div className="flex items-center">
            <div className="pr-2 pb-[1px]">{t("Patch")}</div>
            <Select
              size="small"
              variant="standard"
              value={state.patch}
              onChange={(e) => dispatch(["set_patch", Number(e.target.value)])}
            >
              <MenuItem value={allPatches[0]}>
                {renderPatch(allPatches[0])}
              </MenuItem>
              <MenuItem value={allPatches[1]}>
                {renderPatch(allPatches[1])}
              </MenuItem>
              <MenuItem value={allPatches[2]}>
                {renderPatch(allPatches[2])}
              </MenuItem>
            </Select>
            <div className="pl-4">
              <Select
                size="small"
                variant="standard"
                value={state.rankGroup}
                onChange={(e) =>
                  dispatch(["set_rank_group", Number(e.target.value)])
                }
              >
                <MenuItem value={4}>{t("GM+")}</MenuItem>
                <MenuItem value={0}>{t("Master+")}</MenuItem>
                <MenuItem value={1}>{t("Diamond+")}</MenuItem>
                <MenuItem value={2}>{t("Emerald+")}</MenuItem>
                <MenuItem value={3}>{t("Platinum+")}</MenuItem>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center xl:flex-row xl:items-start xl:justify-start xl:sticky xl:top-[108px] gap-8 justify-center mt-4">
          <div className="w-[260px] md:w-full xl:w-[260px] p-4 self-start rounded bg-bg flex flex-col md:flex-row xl:flex-col gap-6 justify-center sticky top-[108px]">
            <ExFilterSelect
              idx={0}
              value={state.filters[0] || null}
              options={fullData}
              dispatch={dispatch}
              getMatches={getMatches}
              allFilters={state.filters}
            />
            <ExFilterSelect
              idx={1}
              value={state.filters[1] || null}
              options={fullData}
              dispatch={dispatch}
              getMatches={getMatches}
              disabled={state.filters.length < 1}
              allFilters={state.filters}
            />
            <ExFilterSelect
              idx={2}
              value={state.filters[2] || null}
              options={fullData}
              dispatch={dispatch}
              getMatches={getMatches}
              disabled={state.filters.length < 2}
              allFilters={state.filters}
            />
          </div>
          <div className="bg-bg rounded w-screen max-w-[780px] min-h-[240px] py-2 relative">
            <ExDataTable
              queryData={state.queryData}
              onExpand={onExpand}
              dispatch={dispatch}
              hidePlacements={state.hidePlacements}
            />
            {state.loading ? (
              <div className="flex pt-24 justify-center z-50 absolute top-0 left-0 right-0 bottom-0 bg-[#11111155]">
                <CircularProgress size={60} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
